import React, { Component } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import MuiLink from "@mui/material/Link";
import { Link as RouterLink } from "react-router-dom";
import {
  NodeContainer,
  NodeAgentPicker,
  NodePresetChips,
  NodeSection,
  NodeSelect,
  NodeTextField,
  NODE_UI,
} from "../../NodeUI";
import AgentAvatar from "../AgentAvatar";
import { updateManagedRunPolicy } from "./managedRunApi";
import { loadDraftProject } from "../../../utils/canvasApi";
import { send_request } from "../../../utils/Request";
import { withParams } from "../../../Routes";

const PageRoot = styled(Box)(({ theme }) => ({
  boxSizing: "border-box",
  padding: theme.spacing(1.5, 2, 2),
  backgroundColor: theme.palette.background.paper,
}));

const Header = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "row",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HelperText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: NODE_UI.fontSize.caption,
  lineHeight: 1.4,
  marginTop: NODE_UI.spacing.xs,
}));

const DurationRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(90px, 120px) minmax(130px, 1fr)",
  gap: NODE_UI.spacing.sm,
  alignItems: "end",
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const ToggleRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ theme, selected }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  gap: NODE_UI.spacing.lg,
  padding: NODE_UI.spacing.md,
  border: "1px solid",
  borderColor: selected
    ? alpha(theme.palette.primary.main, 0.55)
    : theme.palette.divider,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor:
    selected
      ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07)
      : theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.02)"
      : "#fafafa",
  cursor: "pointer",
  transition: "background-color 0.12s, border-color 0.12s",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.11)
      : alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.09 : 0.04),
  },
  "&:focus-visible": {
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
    outlineOffset: 2,
  },
}));

const ToggleLabel = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.35,
}));

const ToggleDescription = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginTop: 2,
}));

const WakeEventGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: NODE_UI.spacing.md,
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const OperatorToolGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: NODE_UI.spacing.md,
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const WakeEventCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: NODE_UI.spacing.md,
  padding: NODE_UI.spacing.md,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#fafafa",
}));

const WakeEventIcon = styled(Box)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: NODE_UI.borderRadiusSm,
  flexShrink: 0,
  display: "grid",
  placeItems: "center",
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
}));

const WakeEventBody = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
}));

const WakeEventTitle = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.35,
}));

const WakeEventDescription = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginTop: 2,
}));

const WakeEventControl = styled(Box)(() => ({
  marginTop: NODE_UI.spacing.sm,
}));

const ToolName = styled(Typography)(({ theme }) => ({
  display: "inline-block",
  marginTop: NODE_UI.spacing.xs,
  fontFamily: "monospace",
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
}));

const OperatorToolCard = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "selected" && prop !== "interactive" && prop !== "required",
})(({ theme, selected, interactive, required }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: NODE_UI.spacing.md,
  padding: NODE_UI.spacing.md,
  border: "1px solid",
  borderColor: selected
    ? alpha(theme.palette.primary.main, 0.55)
    : theme.palette.divider,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07)
    : theme.palette.mode === "dark"
    ? "rgba(255,255,255,0.02)"
    : "#fafafa",
  cursor: interactive ? "pointer" : "default",
  opacity: selected || required ? 1 : 0.58,
  transition: "background-color 0.12s, border-color 0.12s, opacity 0.12s",
  "&:hover": interactive
    ? {
        borderColor: theme.palette.primary.main,
        backgroundColor: selected
          ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.11)
          : alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.09 : 0.04),
        opacity: 1,
      }
    : undefined,
  "&:focus-visible": interactive
    ? {
        outline: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
        outlineOffset: 2,
      }
    : undefined,
}));

const ToolMetaText = styled(Typography)(({ theme }) => ({
  marginTop: NODE_UI.spacing.xs,
  color: theme.palette.text.secondary,
  fontSize: NODE_UI.fontSize.caption,
  lineHeight: 1.4,
}));

const ActionRow = styled(Stack)(({ theme }) => ({
  justifyContent: "flex-end",
  flexDirection: "row",
  gap: theme.spacing(1),
  paddingTop: NODE_UI.spacing.md,
}));

const TEMPLATE_IDS = {
  PASSIVE_MONITOR: "passive_monitor",
  STANDARD_OPERATOR: "standard_operator",
  HIGH_TOUCH_OPERATOR: "high_touch_operator",
};

const DEFAULT_OPERATOR_AGENT_VALUE = "__workflow86_managed_operator__";

const TEMPLATE_PRESETS = [
  { value: TEMPLATE_IDS.PASSIVE_MONITOR, label: "Passive" },
  { value: TEMPLATE_IDS.STANDARD_OPERATOR, label: "Standard" },
  { value: TEMPLATE_IDS.HIGH_TOUCH_OPERATOR, label: "High touch" },
];

const TEMPLATE_HELP = {
  [TEMPLATE_IDS.PASSIVE_MONITOR]:
    "Low-touch monitoring with fewer scheduled checks.",
  [TEMPLATE_IDS.STANDARD_OPERATOR]:
    "Balanced default with regular scheduled checks.",
  [TEMPLATE_IDS.HIGH_TOUCH_OPERATOR]:
    "More frequent scheduled monitoring for important or high-risk runs.",
};

const TEMPLATE_INSTRUCTIONS = {
  [TEMPLATE_IDS.PASSIVE_MONITOR]:
    "Monitor this workflow quietly. Post concise summaries on schedule and flag urgent failures in the operator chat.",
  [TEMPLATE_IDS.STANDARD_OPERATOR]:
    "Monitor this workflow end to end. Keep the operator chat updated and escalate when progress stalls or a review is needed.",
  [TEMPLATE_IDS.HIGH_TOUCH_OPERATOR]:
    "Actively watch this high-priority workflow. Check in frequently, summarize meaningful changes, and escalate blockers quickly with clear next steps.",
};

const DURATION_UNITS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

const OPERATOR_TOOLS = [
  {
    icon: FormatListBulletedOutlinedIcon,
    title: "List sessions",
    toolName: "list_sessions",
    description: "List workflow sessions when the operator needs run context.",
    required: true,
  },
  {
    icon: FindInPageOutlinedIcon,
    title: "Inspect session",
    toolName: "get_session",
    description: "Read the current workflow run, component statuses, and errors.",
    required: true,
  },
  {
    icon: ReplayOutlinedIcon,
    title: "Retry component",
    toolName: "retry_session_component",
    description: "Retry one failed or out-of-quota component in the same run.",
  },
  {
    icon: RestartAltOutlinedIcon,
    title: "Prepare rerun inputs",
    toolName: "prepare_rerun_inputs",
    description: "Preview rerun inputs before starting a new production run.",
  },
  {
    icon: RestartAltOutlinedIcon,
    title: "Rerun workflow",
    toolName: "rerun_session",
    description: "Start a new production run from this run after review-task approval.",
  },
  {
    icon: StopCircleOutlinedIcon,
    title: "Terminate run",
    toolName: "terminate_session",
    description: "Terminate a running or waiting production run after human approval.",
  },
  {
    icon: PlayCircleOutlineOutlinedIcon,
    title: "Trigger workflow",
    toolName: "trigger_workflow",
    description: "Start a production workflow run after human approval.",
  },
  {
    icon: RateReviewOutlinedIcon,
    title: "Create review task",
    toolName: "review_task",
    description: "Ask a human for approval, input, or a decision in chat.",
    required: true,
  },
];

const REQUIRED_OPERATOR_TOOLS = OPERATOR_TOOLS.filter((tool) => tool.required).map(
  (tool) => tool.toolName
);

const DEFAULT_OPERATOR_TOOLS = OPERATOR_TOOLS.map((tool) => tool.toolName);

const normalizeEnabledTools = (tools) => {
  const requested = Array.isArray(tools) ? tools : DEFAULT_OPERATOR_TOOLS;
  const allowed = new Set(DEFAULT_OPERATOR_TOOLS);
  const normalized = new Set(REQUIRED_OPERATOR_TOOLS);
  requested.forEach((toolName) => {
    if (allowed.has(toolName)) normalized.add(toolName);
  });
  return DEFAULT_OPERATOR_TOOLS.filter((toolName) => normalized.has(toolName));
};

const baseTemplate = (templateId) => ({
  enabled: true,
  templateId,
  instructions:
    TEMPLATE_INSTRUCTIONS[templateId] ||
    TEMPLATE_INSTRUCTIONS[TEMPLATE_IDS.STANDARD_OPERATOR],
  operatorAgentId: null,
  enabledTools: DEFAULT_OPERATOR_TOOLS,
  checkCadence: { enabled: true, interval: "PT12H" },
  summaryCadence: { enabled: true, interval: "P3D" },
  noProgressDeadline: {
    enabled: true,
    durationSinceLastWorkflowStepTransition: "P10D",
  },
  customDeadlines: [],
  terminalStateBehavior: {
    onSuccessful: "CONTINUE_UNTIL_POLICY_STOPS",
    onFailed: "CONTINUE_UNTIL_POLICY_STOPS",
    onTerminated: "STOP_MANAGEMENT",
    onCancelled: "STOP_MANAGEMENT",
  },
});

const TEMPLATES = {
  [TEMPLATE_IDS.PASSIVE_MONITOR]: {
    ...baseTemplate(TEMPLATE_IDS.PASSIVE_MONITOR),
    checkCadence: { enabled: true, interval: "PT24H" },
  },
  [TEMPLATE_IDS.STANDARD_OPERATOR]: baseTemplate(
    TEMPLATE_IDS.STANDARD_OPERATOR
  ),
  [TEMPLATE_IDS.HIGH_TOUCH_OPERATOR]: {
    ...baseTemplate(TEMPLATE_IDS.HIGH_TOUCH_OPERATOR),
    checkCadence: { enabled: true, interval: "PT4H" },
    summaryCadence: { enabled: true, interval: "P1D" },
    noProgressDeadline: {
      enabled: true,
      durationSinceLastWorkflowStepTransition: "P3D",
    },
  },
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const mergePolicy = (defaults, incoming = {}) => {
  const merged = clone(defaults);
  Object.keys(incoming || {}).forEach((key) => {
    if (!(key in merged)) return;
    if (
      incoming[key] &&
      typeof incoming[key] === "object" &&
      !Array.isArray(incoming[key]) &&
      merged[key] &&
      typeof merged[key] === "object" &&
      !Array.isArray(merged[key])
    ) {
      merged[key] = mergePolicy(merged[key], incoming[key]);
    } else if (incoming[key] !== undefined && incoming[key] !== null) {
      merged[key] = incoming[key];
    }
  });
  return merged;
};

const defaultPolicy = () => {
  const policy = clone(TEMPLATES[TEMPLATE_IDS.STANDARD_OPERATOR]);
  policy.enabled = false;
  return policy;
};

const normalizePolicy = (policy) => {
  if (!policy) return defaultPolicy();
  const templateId = policy.templateId || TEMPLATE_IDS.STANDARD_OPERATOR;
  const defaults =
    TEMPLATES[templateId] || TEMPLATES[TEMPLATE_IDS.STANDARD_OPERATOR];
  const normalized = ensureTemplateInstructions(mergePolicy(defaults, policy));
  normalized.operatorAgentId = String(normalized.operatorAgentId || "").trim() || null;
  normalized.enabledTools = normalizeEnabledTools(normalized.enabledTools);
  return normalized;
};

const ensureTemplateInstructions = (policy) => {
  if (!String(policy.instructions || "").trim()) {
    policy.instructions =
      TEMPLATE_INSTRUCTIONS[policy.templateId] ||
      TEMPLATE_INSTRUCTIONS[TEMPLATE_IDS.STANDARD_OPERATOR];
  }
  return policy;
};

const normalizeNumber = (value, min = 1) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return min;
  return Math.max(min, parsed);
};

const durationToIso = (amount, unit) => {
  const safeAmount = normalizeNumber(amount, 1);
  if (unit === "days") return `P${safeAmount}D`;
  if (unit === "minutes") return `PT${safeAmount}M`;
  return `PT${safeAmount}H`;
};

const parseDuration = (value, fallbackUnit = "hours", fallbackAmount = 1) => {
  const text = String(value || "").trim();
  if (!text) return { amount: fallbackAmount, unit: fallbackUnit };

  let match = /^P(\d+)D$/i.exec(text);
  if (match) return { amount: Number(match[1]), unit: "days" };

  match = /^PT(\d+)H$/i.exec(text);
  if (match) return { amount: Number(match[1]), unit: "hours" };

  match = /^PT(\d+)M$/i.exec(text);
  if (match) return { amount: Number(match[1]), unit: "minutes" };

  match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/i.exec(text);
  if (match) {
    const days = Number(match[1] || 0);
    const hours = Number(match[2] || 0);
    const minutes = Number(match[3] || 0);
    const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
    if (totalMinutes > 0) {
      if (totalMinutes % (24 * 60) === 0) {
        return { amount: totalMinutes / (24 * 60), unit: "days" };
      }
      if (totalMinutes % 60 === 0) {
        return { amount: totalMinutes / 60, unit: "hours" };
      }
      return { amount: totalMinutes, unit: "minutes" };
    }
  }

  return {
    amount: fallbackAmount,
    unit: fallbackUnit,
    raw: text,
  };
};

const formatDuration = (value) => {
  const parsed = parseDuration(value);
  if (parsed.raw) return parsed.raw;
  const unit = parsed.amount === 1 ? parsed.unit.slice(0, -1) : parsed.unit;
  return `${parsed.amount} ${unit}`;
};

class ManagedRunPolicy extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      workflowName: "",
      agents: [],
      policy: defaultPolicy(),
      hasChanges: false,
      snackbar: { open: false, message: "", severity: "success" },
    };
  }

  componentDidMount() {
    this.fetchProjectData();
  }

  fetchProjectData = async () => {
    const { id } = this.props.params;
    this.setState({ loading: true });
    try {
      const [projectResult, agentsResult] = await Promise.allSettled([
        loadDraftProject(id),
        send_request("project-service/agent/list", "", {}, "GET"),
      ]);
      if (projectResult.status === "rejected") {
        throw projectResult.reason;
      }
      const project = projectResult.value?.data;
      this.setState({
        workflowName: project?.name || "Untitled",
        agents:
          agentsResult.status === "fulfilled"
            ? agentsResult.value?.data || []
            : [],
        policy: normalizePolicy(project?.extras?.managedRunPolicy),
        loading: false,
        hasChanges: false,
      });
    } catch (error) {
      console.error("Error fetching managed-run policy:", error);
      this.setState({ loading: false });
      this.showSnackbar("Failed to load managed run policy", "error");
    }
  };

  updatePolicy = (updater) => {
    this.setState((prev) => {
      const nextPolicy = clone(prev.policy);
      updater(nextPolicy);
      return { policy: nextPolicy, hasChanges: true };
    });
  };

  handleTemplateChange = (templateId) => {
    this.setState((prev) => {
      const nextPolicy = clone(
        TEMPLATES[templateId] || TEMPLATES[TEMPLATE_IDS.STANDARD_OPERATOR]
      );
      nextPolicy.enabled = prev.policy.enabled;
      nextPolicy.operatorAgentId = prev.policy.operatorAgentId || null;
      return { policy: nextPolicy, hasChanges: true };
    });
  };

  handleSave = async () => {
    const { id } = this.props.params;
    this.setState({ saving: true });
    try {
      await updateManagedRunPolicy(id, this.state.policy);
      this.setState({ saving: false, hasChanges: false });
      this.showSnackbar("Managed run policy saved", "success");
    } catch (error) {
      console.error("Error saving managed-run policy:", error);
      this.setState({ saving: false });
      this.showSnackbar("Failed to save managed run policy", "error");
    }
  };

  handleCancel = () => {
    if (this.props.isModal && this.props.onClose) {
      this.props.onClose();
    } else {
      this.fetchProjectData();
    }
  };

  getSelectedExistingAgent(policy = this.state.policy) {
    if (!policy?.operatorAgentId) return null;
    return (this.state.agents || []).find(
      (agent) => agent.agentId === policy.operatorAgentId
    );
  }

  getOperatorAgentOptions() {
    return [
      {
        agentId: DEFAULT_OPERATOR_AGENT_VALUE,
        name: "Workflow86 managed operator",
        description: "Create and maintain a workflow-specific operator agent automatically.",
        defaultOperator: true,
      },
      ...(this.state.agents || []).map((agent) => ({
        ...agent,
        defaultOperator: false,
      })),
    ];
  }

  getSelectedOperatorOption(policy = this.state.policy) {
    if (!policy?.operatorAgentId) {
      return this.getOperatorAgentOptions()[0];
    }
    return (
      this.getOperatorAgentOptions().find(
        (option) => option.agentId === policy.operatorAgentId
      ) || {
        agentId: policy.operatorAgentId,
        name: "Unavailable agent",
        description: policy.operatorAgentId,
        missing: true,
      }
    );
  }

  showSnackbar = (message, severity) => {
    this.setState({ snackbar: { open: true, message, severity } });
  };

  handleCloseSnackbar = () => {
    this.setState((prev) => ({ snackbar: { ...prev.snackbar, open: false } }));
  };

  renderDurationField(
    label,
    value,
    helperText,
    onChange,
    fallbackUnit = "hours"
  ) {
    const parsed = parseDuration(value, fallbackUnit);
    const updateDuration = (amount, unit) => {
      onChange(durationToIso(amount, unit));
    };

    return (
      <Box>
        <DurationRow>
          <NodeTextField
            label={label}
            value={parsed.amount}
            type="number"
            inputProps={{ min: 1 }}
            onChange={(nextValue) => updateDuration(nextValue, parsed.unit)}
          />
          <NodeSelect
            label="Unit"
            value={parsed.unit}
            options={DURATION_UNITS}
            onChange={(nextUnit) => updateDuration(parsed.amount, nextUnit)}
          />
        </DurationRow>
        {parsed.raw ? (
          <HelperText>
            Saved value was "{parsed.raw}". Choose an amount and unit above to
            replace it with a readable duration.
          </HelperText>
        ) : null}
        {helperText ? <HelperText>{helperText}</HelperText> : null}
      </Box>
    );
  }

  renderToggle(Icon, label, description, checked, onChange) {
    const selected = Boolean(checked);
    const handleToggle = () => onChange(!selected);
    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
        event.preventDefault();
        handleToggle();
      }
    };

    return (
      <ToggleRow
        role="button"
        tabIndex={0}
        selected={selected}
        aria-pressed={selected}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: NODE_UI.spacing.md,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={(theme) => ({
              width: 36,
              height: 36,
              borderRadius: NODE_UI.borderRadiusSm,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              backgroundColor: selected
                ? alpha(theme.palette.primary.main, 0.14)
                : alpha(theme.palette.text.secondary, 0.08),
              color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
            })}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <ToggleLabel>{label}</ToggleLabel>
            {description ? (
              <ToggleDescription>{description}</ToggleDescription>
            ) : null}
          </Box>
        </Box>
      </ToggleRow>
    );
  }

  renderWakeEvent({ icon: Icon, title, description, children }) {
    return (
      <WakeEventCard key={title}>
        <WakeEventIcon>
          <Icon fontSize="small" />
        </WakeEventIcon>
        <WakeEventBody>
          <WakeEventTitle>{title}</WakeEventTitle>
          <WakeEventDescription>{description}</WakeEventDescription>
          {children ? <WakeEventControl>{children}</WakeEventControl> : null}
        </WakeEventBody>
      </WakeEventCard>
    );
  }

  renderAgentOption(props, option) {
    const isDefault = option?.defaultOperator;
    return (
      <Box
        component="li"
        {...props}
        sx={{
          display: "flex !important",
          alignItems: "center",
          gap: 1.25,
          py: 1,
        }}
      >
        <AgentAvatar
          agent={option}
          name={option?.name || "Agent"}
          size={30}
          borderRadius="8px"
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}
            noWrap
          >
            {option?.name || option?.agentId || "Untitled agent"}
          </Typography>
          <Typography
            sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.3 }}
            noWrap
          >
            {option?.description ||
              (isDefault
                ? "Workflow-specific managed operator"
                : option?.agentId || "")}
          </Typography>
        </Box>
      </Box>
    );
  }

  toggleOperatorTool(toolName) {
    if (REQUIRED_OPERATOR_TOOLS.includes(toolName)) return;
    this.updatePolicy((next) => {
      const current = new Set(normalizeEnabledTools(next.enabledTools));
      if (current.has(toolName)) {
        current.delete(toolName);
      } else {
        current.add(toolName);
      }
      next.enabledTools = normalizeEnabledTools(Array.from(current));
    });
  }

  renderOperatorTool(
    { icon: Icon, title, toolName, description, required },
    enabledTools,
    readOnly = false
  ) {
    const selected = enabledTools.includes(toolName);
    const interactive = !required && !readOnly;
    const handleToggle = () => this.toggleOperatorTool(toolName);
    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
        event.preventDefault();
        handleToggle();
      }
    };

    return (
      <OperatorToolCard
        key={toolName}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        selected={selected}
        interactive={interactive}
        required={required}
        aria-pressed={interactive ? selected : undefined}
        onClick={interactive ? handleToggle : undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
      >
        <WakeEventIcon>
          <Icon fontSize="small" />
        </WakeEventIcon>
        <WakeEventBody>
          <WakeEventTitle>{title}</WakeEventTitle>
          <WakeEventDescription>{description}</WakeEventDescription>
          <ToolName>{toolName}</ToolName>
          {readOnly ? (
            <ToolMetaText>
              {selected ? "Enabled on selected agent" : "Not enabled on selected agent"}
            </ToolMetaText>
          ) : required ? (
            <ToolMetaText>Always enabled</ToolMetaText>
          ) : null}
        </WakeEventBody>
      </OperatorToolCard>
    );
  }

  render() {
    const { id } = this.props.params;
    const { isModal } = this.props;
    const {
      loading,
      saving,
      workflowName,
      policy,
      hasChanges,
      snackbar,
    } = this.state;
    const selectedExistingAgent = this.getSelectedExistingAgent(policy);
    const usesExistingAgent = Boolean(policy.operatorAgentId);
    const selectedAgentMissing = usesExistingAgent && !selectedExistingAgent;
    const displayedEnabledTools = usesExistingAgent
      ? selectedExistingAgent?.enabledTools || []
      : normalizeEnabledTools(policy.enabledTools);
    const missingRecommendedTools = usesExistingAgent
      ? REQUIRED_OPERATOR_TOOLS.filter(
          (toolName) => !displayedEnabledTools.includes(toolName)
        )
      : [];

    return (
      <PageRoot sx={isModal ? { pt: 1 } : undefined}>
        {!isModal && (
          <Breadcrumbs separator=">" sx={{ mb: 2 }}>
            <MuiLink component={RouterLink} underline="hover" to="/project">
              Workflows
            </MuiLink>
            <MuiLink
              component={RouterLink}
              underline="hover"
              to={`/project/canvas/${id}`}
            >
              {workflowName || "Workflow"}
            </MuiLink>
            <Typography color="text.primary">Managed run</Typography>
          </Breadcrumbs>
        )}

        {!isModal && (
          <Header>
            <IconButton
              size="small"
              onClick={() => this.props.navigate(`/project/canvas/${id}`)}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
              Managed run for {workflowName || "Workflow"}
            </Typography>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={this.fetchProjectData}
                disabled={loading}
                sx={{ color: "text.secondary" }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Header>
        )}

        {loading ? (
          <NodeContainer sx={{ padding: 0 }}>
            <Skeleton variant="text" width={220} height={24} sx={{ mb: 1 }} />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={220}
              sx={{ borderRadius: 1, mt: 2 }}
            />
          </NodeContainer>
        ) : (
          <NodeContainer sx={{ padding: 0, gap: NODE_UI.spacing.lg }}>
            <NodeSection title="Mode">
              {this.renderToggle(
                SmartToyOutlinedIcon,
                "Enable managed run",
                "New production sessions for this workflow will receive an operator chat.",
                policy.enabled,
                (checked) =>
                  this.updatePolicy((next) => {
                    next.enabled = checked;
                  })
              )}
              <NodePresetChips
                label="Managed mode"
                presets={TEMPLATE_PRESETS}
                selectedValue={policy.templateId || TEMPLATE_IDS.STANDARD_OPERATOR}
                onSelect={this.handleTemplateChange}
              />
              <HelperText>
                {TEMPLATE_HELP[policy.templateId] ||
                  TEMPLATE_HELP[TEMPLATE_IDS.STANDARD_OPERATOR]}
              </HelperText>
            </NodeSection>

            <NodeSection title="Operator agent">
              <NodeAgentPicker
                label="Agent"
                value={this.getSelectedOperatorOption(policy)}
                agents={this.getOperatorAgentOptions()}
                loading={loading}
                placeholder="Search agents..."
                noOptionsText="No agents found"
                inDialog={isModal}
                showCard={Boolean(selectedExistingAgent)}
                renderOption={(props, option) =>
                  this.renderAgentOption(props, option)
                }
                getOptionLabel={(option) =>
                  option?.name || option?.agentId || "Untitled agent"
                }
                onChange={(nextOption) =>
                  this.updatePolicy((next) => {
                    const nextAgentId = nextOption?.agentId;
                    next.operatorAgentId =
                      !nextAgentId || nextAgentId === DEFAULT_OPERATOR_AGENT_VALUE
                        ? null
                        : nextAgentId;
                  })
                }
              />
              <HelperText>
                Use the Workflow86 managed operator to create and maintain a
                workflow-specific agent automatically, or select an existing
                agent to wake with this workflow's managed run schedule and
                context.
              </HelperText>
              {usesExistingAgent && selectedExistingAgent ? (
                <HelperText>
                  Selected agent: {selectedExistingAgent.name || selectedExistingAgent.agentId}.
                  The managed run will use that agent's current prompt, model, and tools.
                </HelperText>
              ) : null}
              {selectedAgentMissing ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  The selected agent is not available. Choose another agent or
                  use the Workflow86 managed operator.
                </Alert>
              ) : null}
              {missingRecommendedTools.length > 0 ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  The selected agent is missing recommended managed run tools:{" "}
                  {missingRecommendedTools.join(", ")}.
                </Alert>
              ) : null}
            </NodeSection>

            <NodeSection title="Instructions">
              <NodeTextField
                label="Operator guidance"
                fullWidth
                multiline
                minRows={4}
                value={policy.instructions || ""}
                onChange={(nextValue) =>
                  this.updatePolicy((next) => {
                    next.instructions = nextValue;
                  })
                }
                placeholder={
                  TEMPLATE_INSTRUCTIONS[policy.templateId] ||
                  TEMPLATE_INSTRUCTIONS[TEMPLATE_IDS.STANDARD_OPERATOR]
                }
                helperText="This is the main instruction the managed run operator follows. Choosing a different mode replaces this with that mode's recommended default."
              />
            </NodeSection>

            <NodeSection title="What wakes the operator">
              <WakeEventGrid>
                {this.renderWakeEvent({
                  icon: ScheduleOutlinedIcon,
                  title: "Health check",
                  description:
                    "Wakes on this interval to inspect the workflow run.",
                  children: this.renderDurationField(
                    "Every",
                    policy.checkCadence.interval,
                    null,
                    (nextValue) =>
                      this.updatePolicy((next) => {
                        next.checkCadence.interval = nextValue;
                      }),
                    "hours"
                  ),
                })}
                {this.renderWakeEvent({
                  icon: TaskAltOutlinedIcon,
                  title: "Summary",
                  description:
                    "Wakes on this interval to post a progress summary in chat.",
                  children: this.renderDurationField(
                    "Every",
                    policy.summaryCadence.interval,
                    null,
                    (nextValue) =>
                      this.updatePolicy((next) => {
                        next.summaryCadence.interval = nextValue;
                      }),
                    "days"
                  ),
                })}
                {this.renderWakeEvent({
                  icon: TimerOffOutlinedIcon,
                  title: "No-progress deadline",
                  description:
                    "Wakes when no workflow step transition has been observed for this long.",
                  children: this.renderDurationField(
                    "After",
                    policy.noProgressDeadline
                      .durationSinceLastWorkflowStepTransition,
                    null,
                    (nextValue) =>
                      this.updatePolicy((next) => {
                        next.noProgressDeadline.durationSinceLastWorkflowStepTransition =
                          nextValue;
                      }),
                    "days"
                  ),
                })}
                {this.renderWakeEvent({
                  icon: ErrorOutlineOutlinedIcon,
                  title: "Workflow event",
                  description:
                    "Wakes after component failure, component termination, workflow success, or workflow termination.",
                })}
              </WakeEventGrid>
              <HelperText>
                Workflow events are coalesced when one is already queued, so the
                operator does not start duplicate checks for the same burst of
                updates.
              </HelperText>
            </NodeSection>

            <NodeSection title="Tools the operator can use">
              <OperatorToolGrid>
                {OPERATOR_TOOLS.map((tool) =>
                  this.renderOperatorTool(
                    tool,
                    displayedEnabledTools,
                    usesExistingAgent
                  )
                )}
              </OperatorToolGrid>
              <HelperText>
                {usesExistingAgent
                  ? "These reflect the selected agent's current tools. Edit that agent to change them."
                  : "These tools are written to the workflow-specific operator agent."}{" "}
                Email sending is not enabled for the default managed run
                operator.
              </HelperText>
            </NodeSection>

            <ActionRow>
              <Button
                variant="outlined"
                color="inherit"
                onClick={this.handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? "Saving..." : "Save policy"}
              </Button>
            </ActionRow>
          </NodeContainer>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={this.handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </PageRoot>
    );
  }
}

export default withParams(ManagedRunPolicy);
